import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { useClaim } from "../../../context/ClaimContext";
import {
  FaExclamationTriangle,
  FaExclamationCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaRedo,
} from "react-icons/fa";
import { SlEqualizer } from "react-icons/sl";
import { useMediaQuery } from "react-responsive";
import DatePicker from "react-datepicker";
import { FiPlus } from "react-icons/fi";
import { formatDateString } from "../../../util_functions/helpers";
import moment from "moment";

const SupportHoursClaimComponent = () => {
  const { clientUser } = useOutletContext();
  const navigate = useNavigate();
  const { claimId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [isUserChangedItemsPerPage, setIsUserChangedItemsPerPage] =
    useState(false);
  const { claimsData, fetchClaimsByClientId, fetchClaimById } = useClaim();
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isFinalized, setIsFinalized] = useState(false);
  const [loading, setLoading] = useState(true);

  const [filterClientName, setFilterClientName] = useState("");
  const [filterSupportStartDate, setFilterSupportStartDate] = useState(null);
  const [filterSupportEndDate, setFilterSupportEndDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState("");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [showFilter, setShowFilter] = useState(false);

  const isGrid = useMediaQuery({
    query: "(min-width: 320px) and (max-width: 1024px)",
  });

  const isSmallScreen = useMediaQuery({
    query: "(min-width: 320px) and (max-width: 768px)",
  });

  const mainContainerRef = useRef(null);
  const filterRef = useRef(null);
  const tableRef = useRef(null);
  const timesheetsTableRef = useRef(null);
  const tableRowRef = useRef(null);
  const paginationRef = useRef(null);

  const gridContainerRef = useRef(null);
  const gridItemRef = useRef(null);

  useEffect(() => {
    if (clientUser) {
      fetchClaimsByClientId(clientUser.id).then(() => setLoading(false));
    }
  }, [clientUser]);

  useEffect(() => {
    const handlePopState = () => {
      setIsModalOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (claimId) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [claimId]);

  useEffect(() => {
    if (claimId && claimsData.length > 0) {
      const claim = claimsData.find((claim) => claim.claimId === claimId);
      if (claim) {
        setSelectedClaim(claim);
        setIsModalOpen(true);
        setIsFinalized(claim.finalized);
        if (claim.timeLeft) {
          setTimeLeft(claim.timeLeft);
        }
      }
    }
  }, [claimId, claimsData]);

  useLayoutEffect(() => {
    function logHeights() {
      const fullHeight = window.innerHeight;
      const tabsHeight = 58;
      const remainingHeight = fullHeight - tabsHeight;

      const filterHeight = filterRef.current?.offsetHeight;
      const paginationHeight = paginationRef.current?.offsetHeight;
      const heightWithNoTabsAndFilter = remainingHeight - filterHeight;
      const tableRowHeight = tableRowRef.current?.offsetHeight;
      const itemsPerPageHeight =
        remainingHeight - filterHeight - paginationHeight;
      const itemsPerPageWidth = gridContainerRef.current?.offsetWidth;

      console.log("Total browser viewport height:", fullHeight, "px");
      console.log("Tabs height:", tabsHeight, "px");
      console.log("filter-overall-container height:", filterHeight);
      console.log(
        "Remaining height without tabs and filter:",
        heightWithNoTabsAndFilter,
        "px"
      );
      console.log("pagination height:", paginationHeight);
      console.log("Table row height:", tableRowHeight, "px");
      if (isGrid) {
        console.log("Is Grid", isGrid);
        console.log("Timesheets table ref width:", itemsPerPageWidth, "px");
        const containerWidth = itemsPerPageWidth;
        const gridItemWidth = gridItemRef.current?.offsetWidth;
        const gridItemHeight = gridItemRef.current?.offsetHeight;

        if (containerWidth && gridItemWidth && gridItemHeight) {
          const columns = Math.floor(containerWidth / gridItemWidth);
          const rows = Math.floor(remainingHeight / gridItemHeight);
          const computed = columns * rows;
          console.log(
            "[updateItemsPerPage - Grid] columns:",
            columns,
            "rows:",
            rows
          );
        }
      }
      console.log(
        "available items per page height no tabs, filter, pagination:",
        itemsPerPageHeight,
        "px"
      );
    }

    logHeights();

    window.addEventListener("resize", logHeights);

    return () => {
      window.removeEventListener("resize", logHeights);
    };
  }, [isGrid]);

  const updateItemsPerPage = useCallback(() => {
    if (isUserChangedItemsPerPage) {
      return;
    }
    const fullHeight = window.innerHeight;
    const tabsHeight = 58;
    const remainingHeight = fullHeight - tabsHeight;

    if (isGrid) {
      const containerWidth = gridContainerRef.current?.offsetWidth;
      const gridItemWidth = gridItemRef.current?.offsetWidth;
      const gridItemHeight = gridItemRef.current?.offsetHeight;

      if (containerWidth && gridItemWidth && gridItemHeight) {
        const columns = Math.floor(containerWidth / gridItemWidth);
        const rows = Math.floor(remainingHeight / gridItemHeight);
        const computed = columns * rows;
        console.log(
          "[updateItemsPerPage - Grid] columns:",
          columns,
          "rows:",
          rows
        );
        setItemsPerPage(computed >= 1 ? computed : 1);
      } else {
        console.log(
          "[updateItemsPerPage - Grid] missing dimensions, defaulting to 1"
        );
        setItemsPerPage(1);
      }
    } else {
      const filterHeight = filterRef.current?.offsetHeight;
      const paginationHeight = paginationRef.current?.offsetHeight;
      const itemsPerPageHeight =
        (remainingHeight - filterHeight - paginationHeight) * 0.65;
      const rowHeight = tableRowRef.current?.offsetHeight;
      if (rowHeight) {
        const computed = Math.floor(itemsPerPageHeight / rowHeight);
        console.log("[updateItemsPerPage - Table] computed rows:", computed);
        setItemsPerPage(computed >= 1 ? computed : 1);
      } else {
        console.log(
          "[updateItemsPerPage - Table] rowHeight undefined, defaulting to 1"
        );
        setItemsPerPage(1);
      }
    }
  }, [isGrid]);

  const filteredClaims = useMemo(() => {
    return claimsData.filter((claim) => {
      if (
        !claim.claimId ||
        !claim.clientName ||
        !claim.workerName ||
        !claim.startDate ||
        !claim.startTime ||
        !claim.endTime ||
        !claim.rate ||
        typeof claim.rate.value !== "number" ||
        !claim.rate.type ||
        !claim.rate.type.trim()
      ) {
        return false;
      }

      const matchesClientName = filterClientName
        ? claim.clientName
            .toLowerCase()
            .includes(filterClientName.toLowerCase())
        : true;

      const matchesSupportDate =
        filterSupportStartDate || filterSupportEndDate
          ? moment(claim.startDate).isBetween(
              filterSupportStartDate,
              filterSupportEndDate,
              undefined,
              "[]"
            )
          : true;

      const matchesStatus = filterStatus ? claim.status === filterStatus : true;

      const matchesInvoiceStatus =
        filterInvoiceStatus === "true"
          ? claim.invoiceSent === true
          : filterInvoiceStatus === "false"
          ? claim.invoiceSent === false
          : true;

      return (
        matchesClientName &&
        matchesSupportDate &&
        matchesStatus &&
        matchesInvoiceStatus
      );
    });
  }, [
    claimsData,
    filterClientName,
    filterSupportStartDate,
    filterSupportEndDate,
    filterStatus,
    filterInvoiceStatus,
  ]);

  const firstItemIndex = (currentPage - 1) * itemsPerPage;
  const lastItemIndex = firstItemIndex + itemsPerPage;
  const currentItems = filteredClaims.slice(firstItemIndex, lastItemIndex);

  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);

  useEffect(() => {
    console.log("Claims data:", claimsData);
  }, [claimsData]);

  useEffect(() => {
    console.log("currentItems:", currentItems);
  }, [currentItems]);

  useEffect(() => {
    console.log("Filtered items count:", filteredClaims.length);
    console.log("Filtered items:", filteredClaims);
  }, [filteredClaims]);

  useLayoutEffect(() => {
    const updateLayout = () => {
      if (isGrid) {
        if (gridItemRef.current && gridItemRef.current.offsetHeight) {
          updateItemsPerPage();
        } else {
          requestAnimationFrame(updateLayout);
        }
      } else {
        if (tableRowRef.current && tableRowRef.current.offsetHeight) {
          updateItemsPerPage();
        } else {
          requestAnimationFrame(updateLayout);
        }
      }
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [filteredClaims, isGrid, updateItemsPerPage]);

  const generateDropdownOptions = () => {
    let base = itemsPerPage;
    let secondOption = Math.ceil((base * 2) / 5) * 5;
    return [base, secondOption, secondOption * 2].filter(
      (value, index, self) => self.indexOf(value) === index
    );
  };

  const dropdownOptions = generateDropdownOptions();

  const handleViewMore = async (claimId) => {
    console.log("Selected claim ID:", claimId);
    const claim = await fetchClaimById(claimId);

    if (!claim) {
      console.error("Failed to fetch claim.");
      return;
    }

    setSelectedClaim(claim);
    setIsModalOpen(true);
    navigate(`/account/fs-dashboard/timesheets-billing/details/${claimId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const localDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60000
    );
    return localDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatHours = (hours) => {
    const hrs = Math.floor(hours);
    const mins = Math.round((hours - hrs) * 60);

    let result = "";

    if (hrs > 0) {
      result += `${hrs} ${hrs === 1 ? "hr" : "hrs"}`;
    }

    if (mins > 0) {
      if (result) result += ", ";
      result += `${mins} ${mins === 1 ? "min" : "mins"}`;
    }

    return result || "0 mins";
  };

  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) {
      return "N/A";
    }
    const parseTime = (timeStr) => {
      if (!timeStr) {
        return { hours: 0, minutes: 0 };
      }
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":");
      if (hours === "12") {
        hours = "00";
      }
      if (modifier === "PM") {
        hours = parseInt(hours, 10) + 12;
      } else {
        hours = parseInt(hours, 10);
      }
      minutes = parseInt(minutes, 10);
      return { hours, minutes };
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);

    const startDate = new Date(0, 0, 0, start.hours, start.minutes);
    const endDate = new Date(0, 0, 0, end.hours, end.minutes);

    let diff = (endDate - startDate) / 1000 / 60 / 60;
    if (diff < 0) {
      diff += 24;
    }

    return diff.toFixed(2);
  };

  const renderRates = (rate) => {
    if (!rate)
      return (
        <>
          <td data-th="Rate / Hour">N/A</td>
          <td data-th="Schedule">No rate type</td>
        </>
      );
    return (
      <>
        <td data-th="Rate / Hour">
          <strong>${rate.value.toFixed(2)}</strong>
        </td>
        <td data-th="Schedule">{rate.type}</td>
      </>
    );
  };

  const handleEdit = (claim) => {
    console.log("Editing claim:", claim);
  };

  const handleDelete = () => {
    console.log("Deleting claim:", selectedClaim);
  };

  const handleUpdate = () => {
    console.log("Updating claim:", selectedClaim);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleClientNameFilterChange = (e) => {
    setFilterClientName(e.target.value);
  };

  const handleSupportStartDateChange = (date) => {
    setFilterSupportStartDate(date);
  };

  const handleSupportEndDateChange = (date) => {
    setFilterSupportEndDate(date);
  };

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleInvoiceStatusFilterChange = (e) => {
    const value = e.target.value;
    setFilterInvoiceStatus(value);
  };

  const handleClearFilters = () => {
    setFilterClientName("");
    setFilterSupportStartDate(null);
    setFilterSupportEndDate(null);
    setFilterStatus("");
    setFilterInvoiceStatus("");
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setIsUserChangedItemsPerPage(true);
    setCurrentPage(1);
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const handlePageChange = (action) => {
    switch (action) {
      case "first":
        setCurrentPage(1);
        break;
      case "previous":
        setCurrentPage((prev) => Math.max(prev - 1, 1));
        break;
      case "next":
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
        break;
      case "last":
        setCurrentPage(totalPages);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={mainContainerRef} className="supportclaim-support-container">
      <div className="supportclaim-header">
        {!isModalOpen && (
          <>
            {isMobile && (
              <div className="filter-toggle-container" onClick={toggleFilter}>
                {showFilter ? (
                  <div className="filter-hide">
                    <FaTimesCircle />
                  </div>
                ) : (
                  <div className="filter-show">
                    <SlEqualizer className="filter-toggle-icon" />
                    <span>Show Filters</span>
                  </div>
                )}
              </div>
            )}
            {(showFilter || !isMobile) && (
              <div ref={filterRef} className="filter-overall-container">
                <div className="wclaim-filter-container">
                  <div className="wclaim-filter-noaddnew-container">
                    <div className="filter-wn-dateselects">
                      <div className="claim-filter-itemwn">
                        <label htmlFor="client-name-filter">
                          Filter by Client
                        </label>
                        <input
                          id="client-name-filter"
                          type="text"
                          placeholder="Client name ..."
                          value={filterClientName}
                          onChange={handleClientNameFilterChange}
                        />
                      </div>
                      <div className="claim-filter-itemds">
                        <label>Date Range</label>
                        <div className="claim-date-selects">
                          <DatePicker
                            selected={filterSupportStartDate}
                            onChange={handleSupportStartDateChange}
                            selectsStart
                            startDate={filterSupportStartDate}
                            endDate={filterSupportEndDate}
                            placeholderText="Start date"
                          />
                          <DatePicker
                            selected={filterSupportEndDate}
                            onChange={handleSupportEndDateChange}
                            selectsEnd
                            startDate={filterSupportStartDate}
                            endDate={filterSupportEndDate}
                            minDate={filterSupportStartDate}
                            placeholderText="End date"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="status-claim-clear-container">
                      <div className="status-claim-status-container">
                        <div className="claim-filter-itemst">
                          <label htmlFor="status-filter">Claim Status</label>
                          <select
                            id="status-filter"
                            value={filterStatus}
                            onChange={handleStatusFilterChange}
                          >
                            <option value="">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                        </div>
                        <div className="claim-filter-itemst">
                          <label htmlFor="invoice-status-filter">
                            Invoice Status
                          </label>
                          <select
                            id="invoice-status-filter"
                            value={filterInvoiceStatus}
                            onChange={handleInvoiceStatusFilterChange}
                          >
                            <option value="">All</option>
                            <option value="true">Sent</option>
                            <option value="false">Not Sent</option>
                          </select>
                        </div>
                      </div>
                      <div className="clear-filters-container">
                        <button
                          className="timesheet-clear-filters-button"
                          onClick={handleClearFilters}
                        >
                          <FaRedo className="timesheet-filter-reset" />
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {filteredClaims.length === 0 ? (
        <div ref={tableRef} className="no-claims-message">
          <p>No claims submitted yet ...</p>
        </div>
      ) : (
        !isModalOpen &&
        !selectedClaim && (
          <div
            ref={isGrid ? gridContainerRef : timesheetsTableRef}
            className="timesheets-table"
          >
            {isGrid ? (
              <div className="timesheet-grid-wrapper">
                {currentItems.map((item, index) => {
                  const hours = calculateHours(item.startTime, item.endTime);
                  const amountDue = item.rate ? item.rate.value * hours : 0;
                  return (
                    <div
                      key={item.id || index}
                      ref={index === 0 ? gridItemRef : null}
                      className="timesheet-grid-item"
                    >
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">Client</span>
                        <span className="timesheet-grid-value">
                          <strong>{item.clientName}</strong>
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Support date
                        </span>
                        <span className="timesheet-grid-value">
                          {formatDate(item.startDate)}
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Session time
                        </span>
                        <span className="timesheet-grid-value">{`${item.startTime} - ${item.endTime}`}</span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">Hours</span>
                        <span className="grid-value">
                          <strong>{formatHours(hours)}</strong>
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Rate / Hour
                        </span>
                        <span className="timesheet-grid-value">
                          <strong>
                            {item.rate
                              ? `$${item.rate.value.toFixed(2)}`
                              : "N/A"}
                          </strong>
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">Schedule</span>
                        <span className="timesheet-grid-value">
                          {item.rate ? item.rate.type : "No rate type"}
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Session amount
                        </span>
                        <span className="timesheet-grid-value">
                          <strong>${amountDue.toFixed(2)}</strong>
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Claim status
                        </span>
                        <span className="timesheet-grid-value">
                          <div className="timesheet-claim-status-container">
                            {item.status}
                            {item.status === "Pending" ? (
                              <FaExclamationCircle className="claim-status-pending" />
                            ) : item.status === "Rejected" ? (
                              <FaTimesCircle className="claim-status-rejected" />
                            ) : (
                              <FaCheckCircle className="grid-claim-status-completed" />
                            )}
                          </div>
                        </span>
                      </div>
                      <div className="timesheet-grid-field">
                        <span className="timesheet-grid-label">
                          Invoice sent
                        </span>
                        <span className="timesheet-grid-value">
                          <strong
                            className={
                              item.invoiceSent ? "grid-yes-text" : "no-text"
                            }
                          >
                            {item.invoiceSent ? "Yes" : "No"}
                          </strong>
                        </span>
                      </div>
                      <div className="grid-field">
                        <button
                          className="grid-view-more-button"
                          onClick={() => handleViewMore(item.claimId)}
                        >
                          View more
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <table
                className={`supportclaim-support-table ${
                  isModalOpen ? "hidden" : ""
                }`}
              >
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Support date</th>
                    <th>Session time</th>
                    <th>Hours</th>
                    <th>Rate / Hour</th>
                    <th>Schedule</th>
                    <th>Session amount</th>
                    <th>Claim status</th>
                    <th>Invoice sent</th>
                    <th>View More</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => {
                    const hours = calculateHours(item.startTime, item.endTime);
                    console.log(
                      "Processing item:",
                      item.startTime,
                      item.endTime
                    );
                    const amountDue = item.rate ? item.rate.value * hours : 0;
                    return (
                      <tr
                        key={item.id || index}
                        ref={index === 0 ? tableRowRef : null}
                      >
                        <td className="client-name-cell" data-th="Client">
                          {item.workerName}
                        </td>
                        <td data-th="Support date">
                          {formatDate(item.startDate)}
                        </td>
                        <td data-th="Session time">{`${item.startTime} - ${item.endTime}`}</td>
                        <td data-th="Hours">
                          <strong>{formatHours(hours)}</strong>
                        </td>
                        {renderRates(item.rate)}
                        <td data-th="Session amount">
                          <strong>${amountDue.toFixed(2)}</strong>
                        </td>
                        <td data-th="Claim status">
                          <div className="claim-status-container">
                            {item.status}
                            {item.status === "Pending" ? (
                              <FaExclamationCircle className="claim-status-pending" />
                            ) : item.status === "Rejected" ? (
                              <FaTimesCircle className="claim-status-rejected" />
                            ) : (
                              <FaCheckCircle className="claim-status-completed" />
                            )}
                          </div>
                        </td>
                        <td
                          className="invoice-sent-style"
                          data-th="Invoice sent"
                        >
                          <strong
                            className={
                              item.invoiceSent ? "yes-text" : "no-text"
                            }
                          >
                            {item.invoiceSent ? "Yes" : "No"}
                          </strong>
                        </td>
                        <td>
                          <button
                            className="view-more-button"
                            onClick={() => handleViewMore(item.claimId)}
                          >
                            View more
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!isModalOpen && !selectedClaim && (
              <div className="items-per-page-selector">
                <label htmlFor="items-per-page">Show items per page</label>
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  {dropdownOptions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!isModalOpen && (
              <div ref={paginationRef} className="active-jobs-pagination">
                <button
                  onClick={() => handlePageChange("first")}
                  disabled={currentPage === 1}
                >
                  &laquo;
                </button>
                <button
                  onClick={() => handlePageChange("previous")}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                <span>
                  Page <strong>{currentPage}</strong> of{" "}
                  <strong>{totalPages > 99 ? "99+" : totalPages}</strong>
                </span>

                <button
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage >= totalPages}
                >
                  &gt;
                </button>
                <button
                  onClick={() => handlePageChange("last")}
                  disabled={currentPage >= totalPages}
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        )
      )}
      {isModalOpen &&
        selectedClaim &&
        (() => {
          const hours = calculateHours(
            selectedClaim.startTime,
            selectedClaim.endTime
          );
          return (
            <div className="claim-details">
              <div className="claim-back-container" onClick={handleBackClick}>
                <FaArrowLeft className="claim-back-icon" /> Back to Claims List
              </div>
              {timeLeft && (
                <div className="claim-time-left">
                  Worksheet editable for <strong>{timeLeft}</strong>, before
                  lock.
                </div>
              )}
              {isSmallScreen ? (
                <div className="timesheets-details-table">
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <strong>Reference Number</strong>
                        </td>
                        <td>{selectedClaim.claimId}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Agreement Ref Number</strong>
                        </td>
                        <td>
                          <a
                            href={`/account/ps-dashboard/agreements/worker/${selectedClaim.agreementId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="agreement-link"
                          >
                            {selectedClaim.agreementId}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Claim Submission Date</strong>
                        </td>
                        <td>
                          {formatDateString(selectedClaim.claimSubmissionDate)}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Client Name</strong>
                        </td>
                        <td className="agreement-link">
                          <strong>{selectedClaim.clientName}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Support Date</strong>
                        </td>
                        <td>{formatDate(selectedClaim.startDate)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Worker Name</strong>
                        </td>
                        <td className="agreement-link">
                          <strong>{selectedClaim.workerName}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Start Time</strong>
                        </td>
                        <td>{selectedClaim.startTime}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>End Time</strong>
                        </td>
                        <td>{selectedClaim.endTime}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Hours</strong>
                        </td>
                        <td>
                          <strong>{formatHours(hours)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Rate / Hour</strong>
                        </td>
                        <td>
                          {selectedClaim.rate ? (
                            <>
                              <strong>
                                ${selectedClaim.rate.value.toFixed(2)}
                              </strong>{" "}
                              ({selectedClaim.rate.type})
                            </>
                          ) : (
                            "No rate"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Claim Status</strong>
                        </td>
                        <td>
                          {selectedClaim.status}
                          {selectedClaim.status === "Pending" ? (
                            <FaExclamationCircle className="claim-status-pending" />
                          ) : selectedClaim.status === "Rejected" ? (
                            <FaTimesCircle className="claim-status-rejected" />
                          ) : (
                            <FaCheckCircle className="claim-status-completed" />
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Invoice Sent</strong>
                        </td>
                        <td>{selectedClaim.invoiceSent ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Invoice Paid</strong>
                        </td>
                        <td>{selectedClaim.invoicePaid ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Session Amount</strong>
                        </td>
                        <td>
                          {selectedClaim.rate
                            ? `$${(
                                selectedClaim.rate.value *
                                calculateHours(
                                  selectedClaim.startTime,
                                  selectedClaim.endTime
                                )
                              ).toFixed(2)}`
                            : "$0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Meet and Greet Session Rate</strong>
                        </td>
                        <td>${selectedClaim.initialGreetingSessionRate}</td>
                      </tr>
                      <tr>
                        <td>
                          <div className="claim-total-amount-text">
                            <strong>Total Amount Due</strong>
                          </div>
                        </td>
                        <td>
                          <div className="claim-total-amount">
                            {selectedClaim.rate
                              ? `$${(
                                  selectedClaim.rate.value *
                                    calculateHours(
                                      selectedClaim.startTime,
                                      selectedClaim.endTime
                                    ) +
                                  parseFloat(
                                    selectedClaim.initialGreetingSessionRate
                                  )
                                ).toFixed(2)}`
                              : `$${parseFloat(
                                  selectedClaim.initialGreetingSessionRate
                                ).toFixed(2)}`}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="timesheets-details">
                  <div className="details-table-left">
                    <table>
                      <tbody>
                        <tr>
                          <td>Reference Number</td>

                          <td>
                            <strong>{selectedClaim.claimId}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Agreement Ref Number</td>
                          <td>
                            <a
                              href={`/account/ps-dashboard/agreements/worker/${selectedClaim.agreementId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="agreement-link"
                            >
                              {selectedClaim.agreementId}
                            </a>
                          </td>
                        </tr>
                        <tr>
                          <td>Claim Submission Date</td>
                          <td>
                            {formatDateString(
                              selectedClaim.claimSubmissionDate
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Client Name</td>
                          <td className="agreement-link">
                            {selectedClaim.clientName}
                          </td>
                        </tr>
                        <tr>
                          <td>Support Date</td>
                          <td>
                            <strong>
                              {formatDate(selectedClaim.startDate)}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Worker Name</td>
                          <td className="agreement-link">
                            {selectedClaim.workerName}
                          </td>
                        </tr>
                        <tr>
                          <td>Start Time</td>
                          <td>
                            <strong>{selectedClaim.startTime}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>End Time</td>
                          <td>
                            <strong>{selectedClaim.endTime}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="details-table-right">
                    <table>
                      <tbody>
                        <tr>
                          <td>Hours</td>
                          <td>
                            <strong>{formatHours(hours)}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Rate / Hour</td>
                          <td>
                            {selectedClaim.rate ? (
                              <>
                                <strong>
                                  ${selectedClaim.rate.value.toFixed(2)}
                                </strong>{" "}
                                ({selectedClaim.rate.type})
                              </>
                            ) : (
                              "No rate"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Claim Status</td>
                          <td>
                            <strong>
                              {selectedClaim.status}
                              {selectedClaim.status === "Pending" ? (
                                <FaExclamationCircle className="claim-status-pending" />
                              ) : selectedClaim.status === "Rejected" ? (
                                <FaTimesCircle className="claim-status-rejected" />
                              ) : (
                                <FaCheckCircle className="claim-status-completed" />
                              )}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Invoice Sent</td>
                          <td>
                            <strong>
                              {selectedClaim.invoiceSent ? "Yes" : "No"}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Invoice Paid</td>
                          <td>
                            <strong>
                              {selectedClaim.invoicePaid ? "Yes" : "No"}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Session Amount</td>
                          <td>
                            <strong>
                              {selectedClaim.rate
                                ? `$${(
                                    selectedClaim.rate.value *
                                    calculateHours(
                                      selectedClaim.startTime,
                                      selectedClaim.endTime
                                    )
                                  ).toFixed(2)}`
                                : "$0.00"}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Meet & Greet Session Rate</td>
                          <td>
                            <strong>
                              ${selectedClaim.initialGreetingSessionRate}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Amount Due</td>
                          <td>
                            <div className="claim-total-amount">
                              {selectedClaim.rate
                                ? `$${(
                                    selectedClaim.rate.value *
                                      calculateHours(
                                        selectedClaim.startTime,
                                        selectedClaim.endTime
                                      ) +
                                    parseFloat(
                                      selectedClaim.initialGreetingSessionRate
                                    )
                                  ).toFixed(2)}`
                                : `$${parseFloat(
                                    selectedClaim.initialGreetingSessionRate
                                  ).toFixed(2)}`}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="edit-button-cell">
                {!selectedClaim.allowedEdit && selectedClaim.isFinalized && (
                  <span className="claim-edit-note">
                    <FaExclamationTriangle className="warning-icon" />
                    <strong>
                      Once submitted and locked, claims cannot be edited. For
                      changes, contact customer support.
                    </strong>
                  </span>
                )}
                <span className="claim-button-container">
                  {selectedClaim.allowedEdit ? (
                    <>
                      <button
                        className="claim-update-button"
                        onClick={handleUpdate}
                      >
                        Edit / Update
                      </button>
                      <button
                        className="claim-delete-button"
                        onClick={handleDelete}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="claim-edit-button"
                      onClick={() => handleEdit(selectedClaim)}
                      disabled={
                        !selectedClaim.isAllowedEdit &&
                        selectedClaim.isFinalized
                      }
                    >
                      Edit Claim
                    </button>
                  )}
                </span>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default SupportHoursClaimComponent;
